import { useEffect, useState } from "react";

export type ScrollDirection = "up" | "down" | null;

export interface UseScrollDirectionOptions {
    /**
     * Minimum scroll position before direction detection starts
     * @default 100
     */
    threshold?: number;
}

/**
 * Hook that detects scroll direction and navigation state
 * @param options Configuration options
 * @returns Object containing scroll state and navigation state
 */
export const useScrollDirection = (options: UseScrollDirectionOptions = {}) => {
    const { threshold = 100 } = options;
    const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);
    const [isNavigationSticky, setIsNavigationSticky] = useState(false);
    const [isNavigationHidden, setIsNavigationHidden] = useState(false);

    useEffect(() => {
        let lastScrollY = window.scrollY;
        let ticking = false;

        const updateState = () => {
            const scrollY = window.scrollY;
            const direction = scrollY > lastScrollY ? "down" : "up";
            const body = document.body;

            // Update scroll direction
            if (direction === "up" && scrollY > threshold) {
                setScrollDirection("up");
            } else {
                setScrollDirection("down");
            }

            // Check navigation state from body classes
            // Note: both classes can be present, but stickyNavigation takes priority
            const hasSticky = body.classList.contains('stickyNavigation');
            const hasHidden = body.classList.contains('hideNavigation');
            
            // stickyNavigation takes priority over hideNavigation
            setIsNavigationSticky(hasSticky);
            setIsNavigationHidden(hasHidden && !hasSticky); // Only hidden if not sticky

            lastScrollY = scrollY > 0 ? scrollY : 0;
            ticking = false;
        };

        const onScroll = () => {
            if (!ticking) {
                requestAnimationFrame(updateState);
                ticking = true;
            }
        };

        // Initial state check
        updateState();

        window.addEventListener("scroll", onScroll);

        return () => {
            window.removeEventListener("scroll", onScroll);
            setScrollDirection(null);
            setIsNavigationSticky(false);
            setIsNavigationHidden(false);
        };
    }, [threshold]);

    return {
        scrollDirection,
        isScrollingUp: scrollDirection === "up",
        isScrollingDown: scrollDirection === "down",
        isNavigationSticky,
        isNavigationHidden
    };
};
